import { Divider, Error_Component } from '../../misc'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
import { useEffect, useState } from 'react'
import { DraggableCardTaskProps } from '../../../interfaces/draggable_types'
import { EmployeeNameEmail } from '../../../interfaces/employee_type'
import { getAllEmployeeNameAndEmail } from '../../../api/employee'

import { DraggableCard } from './Components/Draggable'
import { DynamicTaskDroppableContainer } from './Components/Droppable'
import useTaskDragNDrop from '../../../hooks/useTaskDragNDrop'
import { getAllTaskTemplates } from '../../../api/task_template'
import { useNavigate, useParams } from 'react-router-dom'
import { getProject } from '../../../api/projects'
import { ProjectProps } from '../../../interfaces/project_types'
import { BackButton, OrangeButton } from '../../Buttons'
import { TaskProps } from '../../../interfaces/tasks_types'
import { STARTING_EMPLOYEE_CONTAINERS } from './Interface/DnD'
import CreateTaskForm from './CreateTask'

export type EmployeeContainerProps = {
    employee: EmployeeNameEmail
    tasks: DraggableCardTaskProps[]
}
/**
 * TODO:
 *  - The Employee Containers should have a field for all their tasks
 *  - I could do that but the cards already have a parent field (but what's more efficient?)
 * 
 * This is the main route for the DragNDropTasks page
 * 
 * This page is used to assign tasks to employees
 * 
 * The tasks are displayed in a list of cards, and the employees are displayed in a list of droppable containers
 * 
 * The cards can be dragged and dropped into the droppable containers
 * 
 * The droppable containers have a remove button that will remove the employee from the project
 * 
 */
export default function DragNDropTasks() {
    const { id } = useParams()
    const [employee_containers, setEmployeeContainers] = useState<EmployeeContainerProps[]>(STARTING_EMPLOYEE_CONTAINERS)
    const [employee_options, setEmployeeOptions] = useState<EmployeeNameEmail[]>([])
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [activeId, setActiveId] = useState<string | null>(null)
    const [project, setProject] = useState<ProjectProps | null>(null)
    const navigate = useNavigate()

    const [cards, setCards] = useState<DraggableCardTaskProps[]>([])

    // All the functions for the drag and drop are in the useTaskDragNDrop hook
    const { handleDragEnd, handleAddEmployee, handleRemoveEmployee, handleEmployeeSelect, handleDragStart, handleOnSubmit, handleOnReset } = useTaskDragNDrop({
        cards, setCards, employee_containers, setEmployeeContainers, setErrorMessage, employee_options, setEmployeeOptions, setActiveId, navigate, project_id: id || ''
    })

    useEffect(() => {
        const getBackendData = async () => {
            const employees = await getAllEmployeeNameAndEmail({ for_selection: true, filter_by_hierarchy: 'MANAGERS, TEAM, IT, ADMIN' })
            setEmployeeOptions(employees)

            const taskTemplates = await getAllTaskTemplates()

            // Convert the task templates to the format of the cards
            const cards = taskTemplates.map((task) => ({
                id: task.id,
                title: task.title,
                description: task.description || '',
                category: task.category,
                parent: null,
                selected_employee: null
            }))
            setCards(cards)

            if (id) {
                const project = await getProject(id)
                setProject(project.data)

                // If there are any existing tasks for this project, we need to add them to the cards
                const existingTasks: TaskProps[] = project.data.tasks || []

                if (existingTasks && existingTasks.length > 0) {
                    // This array is to store cards that have been assigned to an employee
                    const newCards = existingTasks.map((task) => ({
                        id: task.task_id || '',
                        title: task.title,
                        description: task.description || '',
                        category: task.category || '',
                        parent: employees.find((emp) => emp.label === task.assigned_to)?.value || null, // For some reason, the assigned_to_email doesn't work
                        selected_employee: task.assigned_to || null
                    }))

                    // This is supposed to combine the existing cards with the cards that haven't been assigned yet (ie the taskTemplates)
                    // Since the newCard's id are the task_ids, we use the name to determine if the card is a new card or an existing card
                    const allCards = [...newCards]
                    cards.forEach((templateCard) => {
                        const isDupe = allCards.some(existingCard =>
                            existingCard.title === templateCard.title &&
                            existingCard.category === templateCard.category
                        )

                        if (!isDupe) {
                            allCards.push(templateCard)
                        }
                    })

                    setCards(allCards)

                    // This checks each task and if the task has an assigned_to, it adds it to the assignedEmployees array
                    // Because of this, we need to make sure that no employee gets repeated
                    let assignedEmployees = existingTasks?.map((task) => task.assigned_to) || []
                    assignedEmployees = [...new Set(assignedEmployees)] // This removes duplicates

                    setEmployeeContainers(assignedEmployees.map((employee) => ({
                        employee: {
                            label: employee,
                            value: employees.find((emp) => emp.label === employee)?.value || '',
                            name: employee,
                            email: employees.find((emp) => emp.label === employee)?.value || ''
                        },
                        tasks: existingTasks
                        .filter(task => task.assigned_to === employee)
                        .map((task) => ({
                            id: task.task_id || '',
                            title: task.title || '',
                            description: task.description || '',
                            category: task.category || '',
                            parent: task.assigned_to || null,
                            selected_employee: task.assigned_to || null
                        }))
                    })))

                    }

            } else {
                setErrorMessage("NO PROJECT ID PROVIDED. NOTHING WILL BE SAVED. PLEASE REFRESH THE PAGE AND TRY AGAIN.")
            }    
        }

        getBackendData()
    }, [])

    return (
        <div>
            <div className='flex flex-col mx-2'>
                <h1>
                    Project: {project?.project_name}
                </h1>
            </div>

            <DnDContainer cards={cards} onDragStart={handleDragStart} onDragEnd={handleDragEnd} setErrorMessage={setErrorMessage} setCards={setCards}>
                <div className='flex items-center justify-center'>
                    {errorMessage && <Error_Component errorString={errorMessage} />}
                </div>

                <div className='flex flex-row'>
                    <DynamicTaskDroppableContainer
                        droppables={employee_containers.map((employee) => employee.employee.value)} // id's should be the emails
                        tasksCards={cards} 
                        employee_options={employee_options} 
                        onRemove={handleRemoveEmployee} 
                        onEmployeeSelect={handleEmployeeSelect}
                    />
                </div>

                {/* This is the overlay that shows the card when it is being dragged */}
                <DragOverlay>   
                    {activeId && 
                    <div className='w-full transition-all duration-300 scale-110 rotate-6'>
                        <DraggableCard
                            key={activeId} 
                            id={activeId} 
                            title={cards.find((card) => card.id === activeId)?.title || ''} 
                            description={cards.find((card) => card.id === activeId)?.description || ''} 
                            category={cards.find((card) => card.id === activeId)?.category || ''}
                            parent={cards.find((card) => card.id === activeId)?.parent || null} 
                            selected_employee={cards.find((card) => card.id === activeId)?.selected_employee || null}
                        />
                    </div>
                    }
                </DragOverlay>

                <div className='flex flex-row items-center justify-center gap-2'>
                    <BackButton />
                    <OrangeButton onClick={handleOnReset}>Reset</OrangeButton>
                    <OrangeButton onClick={handleAddEmployee}>Add Employee</OrangeButton>
                    <OrangeButton onClick={handleOnSubmit}>Finish</OrangeButton>
                </div>
            </DnDContainer>
        </div>
    )
    
}

/**
 * This is the container for the tasks that still need to be assigned
 * 
 * It displays the tasks that still need to be assigned and the tasks that have been assigned
 */
function NonAssignedTaskContainer({cards, setCards, setErrorMessage}: {cards: DraggableCardTaskProps[], setCards: (cards: DraggableCardTaskProps[]) => void, setErrorMessage: (errorMessage: string) => void}) {
    return(
        <div className='flex flex-col w-full h-full p-2 my-2 border rounded-md bg-orange-50 dark:bg-stone-800 border-stone-300 dark:border-stone-700'>
            <AssignedTaskText cards={cards} />
            
            <Divider />

            <div className='min-h-[500px] max-h-[800px] overflow-y-auto'>
                <RenderUnassignedTasks cards={cards} />
            </div>

            <Divider />
            
            <CreateTaskForm cards={cards} setCards={setCards} setErrorMessage={setErrorMessage} />
        </div>
    )
}

function AssignedTaskText({cards}: {cards: DraggableCardTaskProps[]}) {
    return(
        cards.some((card) => card.parent === null) ? 
            <h2 className='mb-2 text-xl font-bold text-red-500'>
            Tasks that still need to be assigned:
            </h2> 
        : <h2 className='mb-2 text-xl font-bold text-green-500'>

            All tasks have been assigned
        </h2>
    )
}

function RenderUnassignedTasks({cards}: {cards: DraggableCardTaskProps[]}) {
    return(
        cards.map((card) => (
            card.parent === null ? 
                <DraggableCard
                    key={card.id} 
                    id={card.id} 
                    title={card.title} 
                    description={card.description} 
                    category={card.category}
                    parent={card.parent} 
                    selected_employee={card.selected_employee}
                /> : null
        ))
    )
}

type DnDContainerProps = {
    children: React.ReactNode
    onDragStart: (event: DragStartEvent) => void
    onDragEnd: (event: DragEndEvent) => void
    cards: DraggableCardTaskProps[]
    setErrorMessage: (errorMessage: string) => void
    setCards: (cards: DraggableCardTaskProps[]) => void
}

function DnDContainer({children, onDragStart, onDragEnd, cards, setErrorMessage, setCards}: DnDContainerProps) {
    return(

        <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div className='flex flex-row h-full p-2 rounded-md bg-stone-50 dark:bg-stone-900'>
                <div className='flex-1 max-w-[18rem]'>
                    <NonAssignedTaskContainer cards={cards} setCards={setCards} setErrorMessage={setErrorMessage} />
                </div>

                <div className='flex-auto overflow-x-auto'> 
                    {children}
                </div>
            </div>
        </DndContext>
    )
}